import { ref, computed, watch } from 'vue'
import { useApi } from './useApi'
import type { CurrencyRate } from '../types/common'
import { log } from '../utils/logger'

const STORAGE_KEY = 'takefav-preferred-currency'

// Global state
const currencyRates = ref<CurrencyRate[]>([])
const preferredCurrency = ref<string>('USD')
const isLoading = ref(false)
const lastUpdate = ref<string | null>(null)

export function useCurrency() {
  const { fetchData, postData } = useApi()

  // Load preferred currency from localStorage
  const loadPreferredCurrency = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        preferredCurrency.value = stored
      }
    } catch (error) {
      log.error('Error loading preferred currency:', error)
    }
  }

  // Save preferred currency to localStorage
  const savePreferredCurrency = (currency: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, currency)
      preferredCurrency.value = currency
    } catch (error) {
      log.error('Error saving preferred currency:', error)
    }
  }

  // Fetch currency rates from API
  const fetchCurrencyRates = async (retryCount = 0) => {
    isLoading.value = true
    try {
      const rates = await fetchData<CurrencyRate[]>('/api/rates')
      if (rates && Array.isArray(rates)) {
        currencyRates.value = rates
        lastUpdate.value = new Date().toISOString()
        log.info(`Loaded ${rates.length} currency rates:`, rates.map(r => r.name).join(', '))
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      log.error('Error fetching currency rates:', error)

      // Retry up to 3 times with exponential backoff
      if (retryCount < 3) {
        log.info(`Retrying currency rates fetch (attempt ${retryCount + 1}/3)...`)
        setTimeout(() => {
          fetchCurrencyRates(retryCount + 1)
        }, 1000 * Math.pow(2, retryCount)) // 1s, 2s, 4s
        return
      }      
    } finally {
      isLoading.value = false
    }
  }

  // Update currency rates from external API
  const updateCurrencyRates = async () => {
    try {
      const result = await postData<{ success: boolean; updated: number; message: string }>('/api/currency-rates/update', {})
      if (result?.success) {
        await fetchCurrencyRates() // Refresh the rates
        log.info(`Updated ${result.updated} currency rates`)
      }
    } catch (error) {
      log.error('Error updating currency rates:', error)
    }
  }

  // No default static rates per policy

  // Convert price from USD to preferred currency
  const convertPrice = (usdPrice: number, targetCurrency?: string): number => {
    const currency = targetCurrency || preferredCurrency.value
    
    if (currency === 'USD') {
      return usdPrice
    }

    // First try to find in loaded rates
    const rate = currencyRates.value.find(r => r.name === currency)
    if (rate) {
      return usdPrice * rate.value
    }
    
    // If not found, fall back to USD per policy
    log.warn(`Currency rate not found for ${currency}, using USD`)
    return usdPrice
  }

  // Format price with currency symbol
  const formatPrice = (price: number, currency?: string): string => {
    const targetCurrency = currency || preferredCurrency.value
    const rate = currencyRates.value.find(r => r.name === targetCurrency)
    const symbol = rate?.symbol || targetCurrency
    
    // Round to 2 decimal places for most currencies, 0 for some
    const decimals = ['JPY', 'KRW'].includes(targetCurrency) ? 0 : 2
    const formattedPrice = price.toFixed(decimals)
    
    // Format based on currency conventions
    if (['USD', 'EUR', 'GBP', 'CAD', 'AUD'].includes(targetCurrency)) {
      return `${symbol}${formattedPrice}`
    } else {
      return `${formattedPrice} ${symbol}`
    }
  }

  // Get converted and formatted price
  const getConvertedPrice = (usdPrice: number, currency?: string): string => {
    const convertedPrice = convertPrice(usdPrice, currency)
    return formatPrice(convertedPrice, currency)
  }

  // Get current currency info
  const currentCurrencyInfo = computed<CurrencyRate | null>(() => {
    const rate = currencyRates.value.find(r => r.name === preferredCurrency.value)
    return rate || null
  })

  // Available currencies
  const availableCurrencies = computed(() => {
    // Only use real, loaded rates; no static defaults
    return currencyRates.value.length > 0
      ? currencyRates.value.sort((a, b) => a.name.localeCompare(b.name))
      : []
  })

  // Initialize currency system
  const initialize = async () => {
    loadPreferredCurrency()
    
    // Add a small delay to ensure backend is ready
    await new Promise(resolve => setTimeout(resolve, 500))
    
    await fetchCurrencyRates()
  }

  // Watch for currency changes and save to localStorage
  watch(preferredCurrency, (newCurrency) => {
    if (newCurrency) {
      localStorage.setItem(STORAGE_KEY, newCurrency)
    }
  })

  return {
    // State
    currencyRates: computed(() => currencyRates.value),
    preferredCurrency: computed(() => preferredCurrency.value),
    currentCurrencyInfo,
    availableCurrencies,
    isLoading: computed(() => isLoading.value),
    lastUpdate: computed(() => lastUpdate.value),

    // Actions
    setPreferredCurrency: savePreferredCurrency,
    convertPrice,
    formatPrice,
    getConvertedPrice,
    fetchCurrencyRates,
    updateCurrencyRates,
    initialize,
  }
}

// Global currency instance for use across the app
export const useGlobalCurrency = useCurrency
